import React, { useState } from 'react';

interface MealPlanOverridePopupProps {
    productName: string;
    extraReason: string;
    guestName?: string;
    roomNumber?: string;
    onCharge: () => void;
    onWaive: (reason: string) => void;
    onCancel: () => void;
}

const MealPlanOverridePopup: React.FC<MealPlanOverridePopupProps> = ({
    productName, extraReason, guestName, roomNumber, onCharge, onWaive, onCancel,
}) => {
    const [selectedOption, setSelectedOption] = useState<'charge' | 'waive' | null>(null);
    const [reason, setReason] = useState('');

    const handleConfirm = () => {
        if (selectedOption === 'charge') {
            onCharge();
        } else if (selectedOption === 'waive' && reason.trim()) {
            onWaive(reason.trim());
        }
    };

    const canConfirm = selectedOption === 'charge' || (selectedOption === 'waive' && reason.trim().length > 0);

    return (
        <>
            <div className='dark-popup-overlay' style={{ zIndex: 9998 }} onClick={onCancel} />
            <div style={{
                position: 'fixed', zIndex: 9999, top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: '#fff', borderRadius: 12, width: 420, maxWidth: '90vw',
                boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{
                    padding: '16px 20px', borderBottom: '1px solid #EAECF0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: '50%',
                            backgroundColor: '#FEF3F2', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 9v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#101828' }}>
                            Extra Charge
                        </span>
                    </div>
                    <button onClick={onCancel} style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                        color: '#98A2B3', fontSize: 18, lineHeight: 1,
                    }}>&times;</button>
                </div>

                {/* Body */}
                <div style={{ padding: '16px 20px' }}>
                    <div style={{ fontSize: 14, color: '#344054', marginBottom: 4 }}>
                        <strong>{productName}</strong>
                    </div>
                    {guestName && (
                        <div style={{ fontSize: 13, color: '#667085', marginBottom: 2 }}>
                            {guestName}{roomNumber ? ` - Room ${roomNumber}` : ''}
                        </div>
                    )}
                    <div style={{
                        fontSize: 13, color: '#B42318', backgroundColor: '#FEF3F2',
                        borderRadius: 6, padding: '8px 10px', marginTop: 8, marginBottom: 16,
                    }}>
                        {extraReason}
                    </div>

                    {/* Options */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <label style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                            border: `1.5px solid ${selectedOption === 'charge' ? '#1643E0' : '#D0D5DD'}`,
                            backgroundColor: selectedOption === 'charge' ? '#F5F8FF' : '#fff',
                        }}>
                            <input type="radio" name="override" checked={selectedOption === 'charge'}
                                onChange={() => setSelectedOption('charge')}
                                style={{ accentColor: '#1643E0' }} />
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 500, color: '#344054' }}>Charge Guest</div>
                                <div style={{ fontSize: 12, color: '#667085' }}>The product will be added as an extra charge</div>
                            </div>
                        </label>

                        <label style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                            border: `1.5px solid ${selectedOption === 'waive' ? '#1643E0' : '#D0D5DD'}`,
                            backgroundColor: selectedOption === 'waive' ? '#F5F8FF' : '#fff',
                        }}>
                            <input type="radio" name="override" checked={selectedOption === 'waive'}
                                onChange={() => setSelectedOption('waive')}
                                style={{ marginTop: 3, accentColor: '#1643E0' }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 500, color: '#344054' }}>Waive Charge</div>
                                <div style={{ fontSize: 12, color: '#667085', marginBottom: 8 }}>Include the product free of charge (requires reason)</div>
                                {selectedOption === 'waive' && (
                                    <textarea
                                        rows={3}
                                        value={reason}
                                        onChange={(e) => { if (e.target.value.length <= 200) setReason(e.target.value); }}
                                        placeholder="Enter reason for waiving the charge..."
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            width: '100%', borderRadius: 6, border: '1px solid #D0D5DD',
                                            padding: '8px 10px', fontSize: 13, resize: 'none',
                                            outline: 'none', fontFamily: 'inherit',
                                        }}
                                        autoFocus
                                    />
                                )}
                            </div>
                        </label>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '12px 20px', backgroundColor: '#F9FAFB',
                    borderTop: '1px solid #EAECF0',
                    display: 'flex', justifyContent: 'flex-end', gap: 8,
                }}>
                    <button onClick={onCancel} className='blue-outline-btn' style={{ borderRadius: 8 }}>
                        Cancel
                    </button>
                    <button onClick={handleConfirm} className='blue-filled-btn'
                        disabled={!canConfirm}
                        style={{
                            borderRadius: 8,
                            opacity: canConfirm ? 1 : 0.5,
                            cursor: canConfirm ? 'pointer' : 'not-allowed',
                        }}>
                        Confirm
                    </button>
                </div>
            </div>
        </>
    );
};

export default MealPlanOverridePopup;
